/**
 * PillRow Controller
 *
 * Manages the centered row of "pill" buttons that floats above the chat input.
 * Pills are optional, standing affordances (non-blocking, persistent, idempotent)
 * that live in a separate host from the in-input action-button takeover — see the
 * governing taxonomy in `project-docs/plans/chat-input-redesign.md`.
 *
 * The registry object shape (`{ id, label, tier, visibleWhen, onClick, priority }`)
 * drives: tier-preemption (a visible Tier-1 pill excludes the Tier-2 set), priority
 * sort, a ResizeObserver that republishes the row height when pills wrap, and an ARIA
 * live-region so assistive tech announces a pill appearing. Still out (deliberately):
 * mobile horizontal-scroll — the row uses `flex-wrap: wrap`, which is fine for a few
 * short pills — and a tier visual distinction, which is moot while preemption keeps
 * Tier-1 and Tier-2 from ever co-rendering.
 */
export class PillRow {
  /**
   * @param {EventBus} eventBus - Event bus for module communication
   * @param {Input} [input] - Input controller. Not used internally in Phase 2; held as
   *   a convenience handle for pill predicates / the Phase-3 agent feed.
   */
  constructor(eventBus, input = null) {
    this.eventBus = eventBus;
    this.input = input;

    /**
     * Registered predicate pills (Tier-1 Back-to-Buttons in Phase 2). Driven by
     * `register()` + a `visibleWhen` predicate. NEVER mutated by the agent feed.
     * @type {Array<{id:string,label:string,tier:number,visibleWhen:Function,onClick:Function,priority:number,className:?string}>}
     */
    this.pills = [];

    /**
     * Agent-surfaced Tier-2 pills (chat-input-redesign Phase 3). Fed via
     * `setAgentPills()` / cleared via `removePill()` — held in a SEPARATE collection
     * so the agent feed can never clobber the registered Tier-1 pill. Each is always
     * "visible" (synthetic `visibleWhen: () => true`); actual display is gated by
     * composer-state + Tier-1 preemption in `_evaluate()`.
     * @type {Array<{id:string,label:string,tier:number,groupId:string,visibleWhen:Function,onClick:Function,priority:number}>}
     */
    this.agentPills = [];

    /**
     * Id of the pill in the "pending" state (queued click during streaming — see
     * `PillDispatch` + pill-streaming-interaction-design.md §6). At most one pill is
     * pending at a time (single pending slot); the pending pill renders filled with an
     * animated perimeter, label unchanged, and doubles as the cancel affordance.
     * @type {string|null}
     */
    this.pendingPillId = null;

    // The shared, always-present chat input. The pill row is mounted as a child of
    // the OUTER `.chat-input` (never `.chat-input__inner`, whose rounded, overflow-bounded
    // box would clip the row). Mirrors `MessageList._createScrollButton`.
    this.chatInputEl = document.querySelector('.chat-input');
    this.rowEl = this._ensureRowElement();

    // Republish the height when the row wraps/resizes. Multiple (or long) pills can
    // wrap to a second line, changing the row height WITHOUT a show/hide cycle — which
    // would otherwise leave `--pill-row-height` (and thus the scroll-to-bottom offset)
    // stale. Guarded for environments without ResizeObserver (older browsers, jsdom).
    this._resizeObserver = null;
    if (this.rowEl && typeof ResizeObserver === 'function') {
      this._resizeObserver = new ResizeObserver(() => {
        if (!this.rowEl.hidden) this._publishHeight(this.rowEl.offsetHeight);
      });
      this._resizeObserver.observe(this.rowEl);
    }

    if (this.eventBus) {
      // Re-evaluate on every signal that can change either the stashed-buttons state
      // or the visible-composer state. Input does NOT subscribe to `page:changed`;
      // the pill row owns that subscription.
      this.eventBus.on('input:stash-changed', () => this._evaluate());
      this.eventBus.on('page:changed', () => this._evaluate());
      this.eventBus.on('mode:changed', () => this._evaluate());
      this.eventBus.on('mode:cleared', () => this._evaluate());
      // The usage-lock overlay toggles the "Upgrade" pill. Quota exhaustion fires no
      // page/mode change, so re-evaluate directly on the disable/enable signals.
      this.eventBus.on('input:disabled', () => this._evaluate());
      this.eventBus.on('input:enabled', () => this._evaluate());
    }

    // PageManager may already have emitted the initial `page:changed` before this
    // controller was constructed, so evaluate once up front.
    this._evaluate();
  }

  /**
   * Register a pill definition.
   *
   * Phase 2 accepts a single Tier-1 pill (Back-to-Buttons). The `{ tier, priority }`
   * fields are stored but unused in Phase 2 — the Phase-3 Tier-2 agent feed
   * (persistent, multi, consumed-on-click) will call this same method with those
   * fields to drive tier-preemption / priority sorting, so the shape is fixed now to
   * avoid changing `register()` later.
   *
   * @param {Object} def
   * @param {string} def.id - Stable identifier for the pill
   * @param {string} def.label - Visible pill text
   * @param {number} [def.tier=2] - Display tier (1 = exclusive/Back-to-Buttons; 2 = agent pills)
   * @param {Function} def.visibleWhen - Predicate returning whether the pill should show
   * @param {Function} def.onClick - Click handler
   * @param {number} [def.priority=0] - Sort weight (Phase-3 only)
   * @param {string} [def.className] - Override the button's class list. Defaults to the
   *   suggestion-pill look; pass a custom class (e.g. the usage-lock "Upgrade" CTA's
   *   dark treatment) for a pill that shares the row's location but not its styling.
   */
  register({ id, label, tier = 2, visibleWhen, onClick, priority = 0, className = null }) {
    if (typeof visibleWhen !== 'function' || typeof onClick !== 'function') {
      throw new Error('PillRow.register requires `visibleWhen` and `onClick` to be functions');
    }
    this.pills.push({ id, label, tier, visibleWhen, onClick, priority, className });
    this._evaluate();
  }

  /**
   * Replace the server-surfaced Tier-2 pill set (chat-input-redesign Phase 3). Mutates
   * ONLY `this.agentPills` — the registered Tier-1 pill is untouched. Deduped by `id`
   * (last wins). Callers pass the complete live set, so replacement remains idempotent
   * while supporting multiple simultaneous terminus pills.
   *
   * @param {Array<{id:string,label:string,onClick:Function,groupId?:string,priority?:number}>} [list]
   *   Pill definitions. Each `onClick` should route through ActionRouter (emit
   *   `action:button-click`), not a raw closure — so the agent-pill action's full
   *   handoff runs. Invalid entries (missing id / non-function onClick) are dropped.
   */
  setAgentPills(list = []) {
    const byId = new Map();
    for (const p of Array.isArray(list) ? list : []) {
      if (!p || !p.id || typeof p.onClick !== 'function') continue;
      byId.set(p.id, {
        id: p.id,
        label: p.label,
        tier: 2,
        groupId: p.groupId ?? p.id,
        visibleWhen: () => true,
        onClick: p.onClick,
        priority: p.priority ?? 0
      });
    }
    this.agentPills = Array.from(byId.values());
    this._evaluate();
  }

  /**
   * Remove a single agent-surfaced pill by `id` (e.g. on click-consume). Mutates ONLY
   * `this.agentPills`; the registered Tier-1 pill is never affected. No-op if absent.
   *
   * @param {string} id - Agent-pill id to remove
   * @param {Object} [opts]
   * @param {boolean} [opts.animate=false] - Animate the pill out (~0.2s) before
   *   removal (the "fired" state). Falls back to immediate removal when the pill's
   *   element isn't rendered or reduced motion is preferred.
   */
  removePill(id, { animate = false } = {}) {
    if (!this.agentPills.some((pill) => pill.id === id)) return;
    if (this.pendingPillId === id) this.pendingPillId = null;

    const finalize = () => {
      const next = this.agentPills.filter((pill) => pill.id !== id);
      if (next.length !== this.agentPills.length) {
        this.agentPills = next;
        this._evaluate();
      }
    };

    const el = this.rowEl?.querySelector?.(`[data-pill-id="${id}"]`);
    const reducedMotion = typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!animate || !el || reducedMotion) {
      finalize();
      return;
    }
    // Fired state: animate out, then drop from the model. A re-render inside the
    // window simply skips the animation (finalize is idempotent).
    el.classList.add('chat-input__pill--leaving');
    el.disabled = true;
    setTimeout(finalize, 200);
  }

  /**
   * Mark a pill as pending (queued click during streaming). At most one pill is
   * pending — the single pending slot lives in `PillDispatch`; this is only the
   * visual. Re-renders the row so the pill picks up the pending perimeter style.
   *
   * @param {string} id - Agent-pill id to mark pending
   */
  setPillPending(id) {
    if (this.pendingPillId === id) return;
    this.pendingPillId = id;
    this._evaluate();
  }

  /** Clear the pending visual (fire, cancel, or context change). */
  clearPillPending() {
    if (this.pendingPillId === null) return;
    this.pendingPillId = null;
    this._evaluate();
  }

  /**
   * @private
   * Find an existing `.chat-input__pill-row` or create one appended to `.chat-input`.
   * @returns {HTMLElement|null}
   */
  _ensureRowElement() {
    if (!this.chatInputEl) return null;
    let row = this.chatInputEl.querySelector('.chat-input__pill-row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'chat-input__pill-row';
      row.hidden = true;
      this.chatInputEl.appendChild(row);
    }
    // A11y: announce pill appearance to assistive tech. `polite` so it never interrupts;
    // a labelled group gives the region context. (Content is injected while the row is
    // un-hidden — see `_evaluate` — so the change lands inside the live a11y subtree.)
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', 'Suggested actions');
    row.setAttribute('aria-live', 'polite');
    return row;
  }

  /**
   * @private
   * Whether the shared input is in a visible-composer state. Gated POSITIVELY to
   * `main--chat` + `main--orientation-initial` (the two states where `.chat-input`
   * is sticky/visible) so the row never leaks onto dashboard / fullpage / section
   * pages, yet still covers orientation's text-fallback restore path.
   * @returns {boolean}
   */
  _isComposerState() {
    const main = document.querySelector('.main');
    if (!main) return false;
    return main.classList.contains('main--chat') ||
      main.classList.contains('main--orientation-initial');
  }

  /**
   * @private
   * Evaluate a predicate without letting a throwing predicate break the whole row.
   * @param {{visibleWhen:Function}} pill
   * @returns {boolean}
   */
  _isVisible(pill) {
    try {
      return !!pill.visibleWhen();
    } catch (_err) {
      return false;
    }
  }

  /**
   * @private
   * Recompute the visible pill set and render/hide the row. The row is shown only
   * when in a composer state AND at least one pill's predicate is true. On show the
   * row height is measured and published as `--pill-row-height` (so the
   * scroll-to-bottom button rides above it); on empty the row is hidden and the var
   * is reset to `0px`.
   *
   * Tier-preemption (D5): when a Tier-1 registered pill (Back-to-Buttons) is visible,
   * the Tier-2 agent pills are suppressed — the exclusive stashed-buttons affordance
   * takes the row. The rendered set is ordered by tier then priority (higher first).
   */
  _evaluate() {
    if (!this.rowEl) return;

    let visible = [];
    if (this._isComposerState()) {
      const registeredVisible = this.pills.filter((pill) => this._isVisible(pill));
      // A visible Tier-1 pill preempts the whole Tier-2 agent set.
      const tier1Present = registeredVisible.some((pill) => pill.tier === 1);
      const agentVisible = tier1Present
        ? []
        : this.agentPills.filter((pill) => this._isVisible(pill));
      visible = [...registeredVisible, ...agentVisible];
    }

    if (visible.length === 0) {
      this.rowEl.replaceChildren();
      this.rowEl.hidden = true;
      this._publishHeight(0);
      return;
    }

    // Order: Tier-1 (exclusive) ahead of Tier-2, then by priority (higher first).
    // Tiers never co-render today (preemption), so in practice this orders multiple
    // agent pills; the tier key keeps ordering correct if preemption is ever relaxed.
    // Stable sort preserves registration order for equal keys.
    visible.sort((a, b) => (a.tier - b.tier) || ((b.priority || 0) - (a.priority || 0)));

    // Un-hide BEFORE rendering so the aria-live region is already in the accessibility
    // tree when the pills are injected — otherwise their appearance isn't announced.
    this.rowEl.hidden = false;
    this._render(visible);
    this._publishHeight(this.rowEl.offsetHeight);
  }

  /**
   * @private
   * Render the visible pill set into the row. Reuses `.btn--suggestion` (the existing
   * rounded, clickable pill) as the styling base.
   * @param {Array} visible
   */
  _render(visible) {
    const frag = document.createDocumentFragment();
    for (const pill of visible) {
      const btn = document.createElement('button');
      btn.type = 'button';
      // Default suggestion-pill look; a registered pill may override it (e.g. the
      // usage-lock "Upgrade" CTA keeps its own dark treatment).
      btn.className = pill.className || 'btn--suggestion chat-input__pill';
      btn.dataset.pillId = pill.id;
      const pending = pill.id === this.pendingPillId;
      // Pending state (queued click during streaming): filled style + animated
      // perimeter, label unchanged (no width jump). The pill stays clickable — the
      // click routes back through PillDispatch, where a re-click cancels the action.
      if (pending) {
        btn.classList.add('chat-input__pill--pending');
        btn.title = 'Queued — click again to cancel';
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('aria-label', `${pill.label}, queued. Click again to cancel.`);
      }

      // Fixed-width pills can replace their action label with a concise waiting
      // state without shifting this pill or its neighbors. The original action
      // remains available to assistive technology through the pending aria-label.
      const label = document.createElement('span');
      label.className = 'chat-input__pill-label';
      label.textContent = pending ? 'Queued…' : pill.label;
      btn.appendChild(label);
      btn.addEventListener('click', () => {
        try {
          pill.onClick();
        } catch (_err) { /* a bad handler must not break the row */ }
      });
      frag.appendChild(btn);
    }
    this.rowEl.replaceChildren(frag);
  }

  /**
   * @private
   * Publish the row height so the scroll-to-bottom button offsets above it. Sets the
   * var on `.chat-input` (parent of both the row and the scroll button). Always
   * writes an explicit value — 0px on hide — so the offset never sticks (unlike
   * DashboardManager's `if(height > 0)` guard, which would leave it non-zero).
   * @param {number} px
   */
  _publishHeight(px) {
    this.chatInputEl?.style.setProperty('--pill-row-height', `${px}px`);
  }

  /**
   * Disconnect the ResizeObserver. The controller normally lives for the whole app
   * session, so this is for tests / defensive teardown (e.g. a future re-mount).
   */
  destroy() {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
  }
}
